import { Box, Button, TextField, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { LoadingButton } from "@mui/lab";
import { toast } from "react-toastify";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import AttachFile from "@mui/icons-material/AttachFile";
import { MuiTelInput } from "mui-tel-input";
import { API_ENDPOINT } from "../utils/constants";
import { getDbHeader, toastError } from "../utils/util";
import MuPb from "../widgets/MuPb";

const ServicePerson = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [exhibitor, setExhibitor] = useState();
  const [selectedImage, setSelectedImage] = useState(null);
  const [phoneValue, setPhoneValue] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    mobileNumber: "",
    serviceBadgeType: "SERVICE BADGE",
  });

  useEffect(() => {
    document.title = "SERVICE BADGE";
    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadUser() {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_ENDPOINT}/onsite/get-service-badge-person?id=${id}`,
        getDbHeader()
      );
      if (response.status === 200) {
        setExhibitor(response.data);
      } else if (response.status === 204) {
        toast.info("No Exhibitor Found with that ID.");
      }
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  }

  const onClickSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error("Please enter a name.");
      return;
    }

    const phoneWithoutSpaces = phoneValue.replace(/\s+/g, "");
    if (phoneWithoutSpaces.length < 10) {
      toast.error(
        "Please enter a valid mobile number with at least 10 digits."
      );
      return;
    }

    if (!selectedImage) {
      toast.error("Please select an image.");
      return;
    }

    setUploading(true);

    // Combine both file and form data into a single payload
    const formPayload = new FormData();
    formPayload.append("file", selectedImage);
    formPayload.append("name", formData.name);
    formPayload.append("exhibitorId", exhibitor.exhibitorId);
    formPayload.append("companyName", exhibitor.companyName);
    formPayload.append("hallNumber", exhibitor.hallNumber);
    formPayload.append("standNumber", exhibitor.standNumber);
    formPayload.append("mobileNumber", phoneWithoutSpaces);
    formPayload.append("serviceBadgeType", formData.serviceBadgeType);

    // Get your auth headers and append the multipart content type
    const config = getDbHeader();
    config.headers = {
      ...config.headers,
      "Content-Type": "multipart/form-data",
    };

    try {
      const response = await axios.post(
        `${API_ENDPOINT}/onsite/submit-service-person`,
        formPayload,
        config
      );

      if (response.status === 200) {
        toast.success("Submission Successful");
        navigate(`/service-badge-welcome/${response.data.data._id}`);
      }
    } catch (error) {
      toastError(error);
    } finally {
      setUploading(false);
    }
  };

  const handleImageChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedImage(event.target.files[0]);
    }
  };

  const handleChange = (event) => {
    setFormData({ ...formData, [event.target.name]: event.target.value });
  };

  const handlePhoneChange = (newValue) => {
    setPhoneValue(newValue);
  };

  if (loading) {
    return <MuPb />;
  }

  return (
    <Box mt={2} width="100%" maxWidth={420} mx="auto">
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
          Exhibitor Details
        </Typography>

        <Typography sx={{ fontWeight: "bold" }}>
          Exhibitor ID:{" "}
          <span style={{ fontWeight: "normal" }}>{exhibitor?.exhibitorId}</span>
        </Typography>
        <Typography sx={{ fontWeight: "bold" }}>
          Company Name:{" "}
          <span style={{ fontWeight: "normal" }}>{exhibitor?.companyName}</span>
        </Typography>
        <Typography sx={{ fontWeight: "bold" }}>
          Hall Number:{" "}
          <span style={{ fontWeight: "normal" }}>{exhibitor?.hallNumber}</span>
        </Typography>
        <Typography sx={{ fontWeight: "bold" }}>
          Stand Number:{" "}
          <span style={{ fontWeight: "normal" }}>{exhibitor?.standNumber}</span>
        </Typography>

        <TextField
          fullWidth
          label="Enter Name"
          margin="normal"
          name="name"
          value={formData.name}
          onChange={handleChange}
        />

        <MuiTelInput
          sx={{ mt: 2 }}
          fullWidth
          defaultCountry="IN"
          value={phoneValue}
          onChange={handlePhoneChange}
        />

        <TextField
          sx={{ mt: 3 }}
          fullWidth
          label="Service Badge Type"
          margin="normal"
          name="serviceBadgeType"
          disabled={true}
          value={formData.serviceBadgeType}
        />

        <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 2 }}>
          <Button
            variant="contained"
            component="label"
            startIcon={<AttachFile />}
          >
            Select from Files
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleImageChange}
            />
          </Button>

          <Button
            variant="contained"
            component="label"
            sx={{
              backgroundColor: "#972a4c",
              "&:hover": {
                backgroundColor: "#7a1e3a",
              },
            }}
            startIcon={<PhotoCamera />}
          >
            Launch Camera
            <input
              type="file"
              hidden
              accept="image/*"
              capture="environment"
              onChange={handleImageChange}
            />
          </Button>
        </Box>

        {selectedImage && (
          <img
            src={URL.createObjectURL(selectedImage)}
            alt="Preview"
            style={{
              width: "120px",
              marginLeft: "auto",
              marginRight: "auto",
              display: "block",
              marginTop: "10px",
            }}
          />
        )}

        <LoadingButton
          fullWidth
          loading={uploading}
          sx={{
            mt: 3,
            backgroundColor: "#0074d9",
            "&:hover": {
              backgroundColor: "#005ca9",
            },
          }}
          onClick={onClickSubmit}
          variant="contained"
        >
          Submit
        </LoadingButton>
      </Box>
    </Box>
  );
};

export default ServicePerson;
